import React from 'react';
import { connect } from 'react-redux';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import propTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MuiTitle } from 'src/ui/mui-ui-kit';
import { ComplianceDetailEditForm, ComplianceDetailLog } from '../components';
import { getToken } from '../../auth';
import { getUser, impersonateAccount } from '../../user';
import { fetchAccount, fetchFormData } from '../../account';
import {
  acknowledgeBackgroundAssessment,
  fetchBackgroundAssessment,
  fetchBackgroundAssessmentLogs,
  fetchBackgroundAssessmentLogTypes,
  getComplianceDashboardData,
  getBackgroundAssessmentLog,
  resolveBackgroundAssessment,
  updateBackgroundAssessmentSupplementalInformation,
} from '../redux';
import {
  InnerLoader,
  reduceServerErrors,
  setBreadcrumbs,
  useScrollToTop,
} from '../../ui';

export const DumbComplianceDetail = ({
  id,
  user,
  loading,
  fromDashboard,
  backgroundAssessment,
  backgroundAssessmentLog,
  impersonateAccount,
  fetchAccount,
  fetchFormData,
}) => {
  const [unsavedLogs, setUnsavedLogs] = React.useState([]);
  const navigate = useNavigate();
  const scrollToTop = useScrollToTop();

  React.useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  return (
    <>
      <MuiTitle isSize="4" hasTextColor="info" tag="h2">
        Background Assessment Detail
        <NavLink
          className="close-page button is-small is-text is-pulled-right"
          to="/compliance"
          onClick={() => {
            if (fromDashboard) {
              navigate(-1);
            }
            return !fromDashboard;
          }}
        >
          <span className="icon has-text-danger">
            <FontAwesomeIcon icon={['fal', 'times-circle']} size="2x" />
          </span>
        </NavLink>
      </MuiTitle>
      {!loading && (
        <>
          <ComplianceDetailEditForm
            id={id}
            user={user}
            backgroundAssessment={backgroundAssessment.backgroundAssessment}
            backgroundAssessmentLog={backgroundAssessmentLog}
            unsavedLogs={unsavedLogs}
            setUnsavedLogs={setUnsavedLogs}
            impersonateAccount={impersonateAccount}
            fetchAccount={fetchAccount}
            fetchFormData={fetchFormData}
          />
          <div style={{ height: '2em' }} />
          <ComplianceDetailLog
            unsavedLogs={unsavedLogs}
            backgroundAssessmentLog={backgroundAssessmentLog}
            onEditUnsavedLog={(i, newLog) => {
              const newUnsavedLogs = [...unsavedLogs];
              newUnsavedLogs.splice(i, 1);
              newUnsavedLogs.splice(i, 0, newLog);
              setUnsavedLogs(newUnsavedLogs);
            }}
            onDeleteUnsavedLog={i => {
              const newUnsavedLogs = [...unsavedLogs];
              newUnsavedLogs.splice(i, 1);
              setUnsavedLogs(newUnsavedLogs);
            }}
          />
        </>
      )}
      <InnerLoader visible={loading} style={{ marginTop: 160 }} />
    </>
  );
};

const SmartComplianceDetail = ({
  token,
  user,
  fromDashboard,
  setBreadcrumbs,
  backgroundAssessment,
  backgroundAssessmentLog,
  fetchBackgroundAssessment,
  fetchBackgroundAssessmentLogs,
  fetchBackgroundAssessmentLogTypes,
  impersonateAccount,
  fetchAccount,
  fetchFormData,
}) => {
  const { id: idParam } = useParams();
  const id = parseInt(idParam, 10);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      if (token.isValid()) {
        await setBreadcrumbs([
          {
            label: 'Compliance',
            href: '/compliance',
          },
          {
            label: 'Edit Background Assessment',
            href: `/compliance/background_assessment/edit/${id}`,
          },
        ]);
        await fetchBackgroundAssessmentLogs({
          backgroundAssessmentId: id,
        });
        if (backgroundAssessmentLog.isError()) {
          await reduceServerErrors(backgroundAssessmentLog);
        } else {
          await fetchBackgroundAssessment(id);
          if (backgroundAssessment.isError()) {
            await reduceServerErrors(backgroundAssessment);
          }
        }
      }
      await fetchBackgroundAssessmentLogTypes();
      setLoading(false);
    })();
  }, [id]);

  return backgroundAssessment.backgroundAssessment &&
    backgroundAssessmentLog.backgroundAssessmentLogs &&
    backgroundAssessmentLog.backgroundAssessmentLogTypeList ? (
    <DumbComplianceDetail
      id={id}
      user={user}
      loading={loading}
      fromDashboard={fromDashboard}
      backgroundAssessment={backgroundAssessment}
      backgroundAssessmentLog={backgroundAssessmentLog}
      acknowledgeBackgroundAssessment={acknowledgeBackgroundAssessment}
      resolveBackgroundAssessment={resolveBackgroundAssessment}
      impersonateAccount={impersonateAccount}
      fetchAccount={fetchAccount}
      fetchFormData={fetchFormData}
    />
  ) : null;
};

const mapStateToProps = state => ({
  token: getToken(state),
  user: getUser(state),
  backgroundAssessment: getComplianceDashboardData(state),
  backgroundAssessmentLog: getBackgroundAssessmentLog(state),
});

export const ComplianceDetailPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchBackgroundAssessment,
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
  updateBackgroundAssessmentSupplementalInformation,
  fetchBackgroundAssessmentLogs,

  fetchBackgroundAssessmentLogTypes,
  impersonateAccount,
  fetchAccount,
  fetchFormData,
})(SmartComplianceDetail);

DumbComplianceDetail.propTypes = {
  id: propTypes.number.isRequired,
  user: propTypes.object.isRequired,
  fromDashboard: propTypes.bool,
  loading: propTypes.bool.isRequired,
  backgroundAssessment: propTypes.object.isRequired,
  backgroundAssessmentLog: propTypes.object.isRequired,
  impersonateAccount: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
};

DumbComplianceDetail.defaultProps = {
  fromDashboard: false,
};

SmartComplianceDetail.propTypes = {
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  fromDashboard: propTypes.bool,
  fetchBackgroundAssessment: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  backgroundAssessment: propTypes.object.isRequired,
  backgroundAssessmentLog: propTypes.object.isRequired,
  fetchBackgroundAssessmentLogs: propTypes.func.isRequired,
  fetchBackgroundAssessmentLogTypes: propTypes.func.isRequired,
  impersonateAccount: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
};

SmartComplianceDetail.defaultProps = {
  ...DumbComplianceDetail.defaultProps,
};
